export type DebugTuning = {
  startBpm: number;
  bpmPerRound: number;
  baseBallSpeed: number;
  paddleSpeed: number;
  exposure: number;
  maxDpr: number;
  noteShelfLife: number;
  bokehFocus: number;
  bokehAperture: number;
  bokehMaxBlur: number;
  gameBloomStrength: number;
  gameBloomRadius: number;
  gameBloomThreshold: number;
  headerBloomStrength: number;
  headerBloomRadius: number;
  headerBloomThreshold: number;
};

/** Settings panel — DOM-driven so it can share the sample panel's dark
 *  drawer + slider style. Every input writes back to the tuning object
 *  in place, and exposure / DPR fire the onChange callback so the
 *  renderer picks them up live. */
export class DebugTools {
  private readonly panel: HTMLElement | null;
  private readonly appEl: HTMLElement | null;
  private visible = false;

  constructor(private readonly tuning: DebugTuning, onChange: () => void) {
    this.panel = document.querySelector<HTMLElement>('#settings-panel');
    this.appEl = document.querySelector<HTMLElement>('#app');

    this.bindRange('#setting-start-bpm', 'startBpm', 0);
    this.bindRange('#setting-bpm-per-round', 'bpmPerRound', 0);
    this.bindRange('#setting-ball-speed', 'baseBallSpeed', 1);
    this.bindRange('#setting-paddle-speed', 'paddleSpeed', 1);
    this.bindRange('#setting-exposure', 'exposure', 2, onChange);
    this.bindRange('#setting-max-dpr', 'maxDpr', 1, onChange);
    // Note Life: 1-20 step 1 so 1 and 2 are pickable at the low end. The last
    // tick (20) is the "forever" sentinel, rendered as ∞ and treated as
    // unlimited in Game.
    this.bindRange('#setting-note-shelf-life', 'noteShelfLife', 0, undefined, (v) => (v >= 20 ? '∞' : v.toFixed(0)));
    this.bindRange('#setting-bokeh-focus', 'bokehFocus', 1, onChange);
    this.bindRange('#setting-bokeh-aperture', 'bokehAperture', 4, onChange);
    this.bindRange('#setting-bokeh-maxblur', 'bokehMaxBlur', 3, onChange);
    this.bindRange('#setting-game-bloom-strength', 'gameBloomStrength', 2, onChange);
    this.bindRange('#setting-game-bloom-radius', 'gameBloomRadius', 2, onChange);
    this.bindRange('#setting-game-bloom-threshold', 'gameBloomThreshold', 2, onChange);
    this.bindRange('#setting-header-bloom-strength', 'headerBloomStrength', 2, onChange);
    this.bindRange('#setting-header-bloom-radius', 'headerBloomRadius', 2, onChange);
    this.bindRange('#setting-header-bloom-threshold', 'headerBloomThreshold', 2, onChange);

    // Tap the panel header to close on mobile — matches the ✕ hint that the
    // sample panel uses.
    this.panel?.querySelector('header')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).tagName === 'HEADER' || (e.target as HTMLElement).tagName === 'STRONG') {
        this.setHidden(true);
      }
    });

    // Outside click: any pointer press that lands outside the panel and
    // outside the settings button closes the drawer. Attached permanently
    // and gated on `this.visible` so we don't waste work when it's already
    // hidden. Uses pointerdown so the close fires the moment intent
    // registers, not after the click event bubbles.
    document.addEventListener('pointerdown', (e) => {
      if (!this.visible || !this.panel) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (this.panel.contains(target)) return;
      // Don't close if the settings button itself was clicked — that path
      // owns the toggle and would otherwise fire close-then-open, which
      // looks like nothing happened.
      const settingsBtn = document.querySelector('#settings-button');
      if (settingsBtn && settingsBtn.contains(target)) return;
      this.setHidden(true);
    });
  }

  private bindRange<K extends keyof DebugTuning>(
    selector: string,
    key: K,
    decimals: number,
    onChange?: () => void,
    format?: (v: number) => string,
  ): void {
    const input = document.querySelector<HTMLInputElement>(selector);
    const output = document.querySelector<HTMLOutputElement>(`${selector}-value`);
    if (!input) return;
    const initial = this.tuning[key] as number;
    input.value = String(initial);
    const render = (v: number) => (format ? format(v) : v.toFixed(decimals));
    if (output) output.textContent = render(initial);
    input.addEventListener('input', () => {
      const v = Number(input.value);
      (this.tuning[key] as number) = v;
      if (output) output.textContent = render(v);
      onChange?.();
    });
  }

  setHidden(hidden: boolean): void {
    this.visible = !hidden;
    if (!this.panel || !this.appEl) return;
    if (hidden) {
      this.appEl.removeAttribute('data-settings-open');
      this.panel.hidden = true;
    } else {
      this.panel.hidden = false;
      // Defer to next frame so the CSS transition catches.
      requestAnimationFrame(() => this.appEl!.setAttribute('data-settings-open', '1'));
    }
  }

  toggle(): void {
    this.setHidden(this.visible);
  }

  dispose(): void {
    // No lil-gui to tear down; DOM listeners live for the page lifetime.
  }
}
